import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  CircularProgress,
  Alert,
  Stack,
  Autocomplete,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableContainer,
} from "@mui/material";
import API from "../api";
import InfoIcon from "@mui/icons-material/Info";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

// Helper function to format numbers
function formatNumber(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function ReconciliationReportPage() {
  const [reconciliations, setReconciliations] = useState([]);
  const [selectedRecon, setSelectedRecon] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingRecons, setLoadingRecons] = useState(true);
  const [error, setError] = useState("");
  const [expandedSection, setExpandedSection] = useState("system");

  // Fetch all reconciliations on mount
  useEffect(() => {
    fetchReconciliations();
  }, []);

  const fetchReconciliations = async () => {
    try {
      setLoadingRecons(true);
      const res = await API.get("/reconciliations", {
        params: { limit: 1000, status: "Posted" },
      });
      setReconciliations(res.data.data || []);
    } catch (err) {
      console.error("Error fetching reconciliations", err);
      setError("Failed to load reconciliations");
    } finally {
      setLoadingRecons(false);
    }
  };

  const generateReport = async () => {
    if (!selectedRecon) {
      setError("Please select a reconciliation");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await API.get("/advance-reports/reconciliation-report", {
        params: { reconciliationId: selectedRecon._id },
      });
      setReportData(res.data);
      setExpandedSection("system"); // Auto-expand first section
    } catch (err) {
      console.error("Error generating report", err);
      setError(err.response?.data?.message || "Failed to load report");
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedRecon) {
      setError("Please select a reconciliation");
      return;
    }

    try {
      const response = await API.get("/advance-reports/reconciliation-report/export", {
        params: { reconciliationId: selectedRecon._id },
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `reconciliation-detailed-report-${selectedRecon._id}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error exporting CSV", err);
      setError("Failed to export CSV");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, justifyContent: "center" }}>
        <AssessmentIcon sx={{ fontSize: 40, mr: 2, color: "primary.main" }} />
        <Typography variant="h4">Reconciliation Detail Report</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Complete breakdown showing how System Balance, Actual Cash, and Variance were calculated
      </Typography>

      {/* Selection Panel */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <Autocomplete
            options={reconciliations}
            getOptionLabel={(option) =>
              `${option.account?.accountName || "Unknown"} - ${
                option.date ? new Date(option.date).toLocaleDateString() : ""
              } (${option.status})`
            }
            value={selectedRecon}
            onChange={(event, newValue) => setSelectedRecon(newValue)}
            loading={loadingRecons}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Reconciliation"
                variant="outlined"
                helperText="Choose a posted reconciliation to view its detailed report"
              />
            )}
            sx={{ flexGrow: 1, minWidth: 300 }}
          />
          <Button
            variant="contained"
            onClick={generateReport}
            disabled={!selectedRecon || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
          >
            Generate Report
          </Button>
          <Button
            variant="outlined"
            onClick={handleExportCSV}
            disabled={!selectedRecon || !reportData}
            startIcon={<DownloadIcon />}
          >
            Export CSV
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Calculating complete reconciliation breakdown...
          </Typography>
        </Box>
      ) : reportData ? (
        <>
          {/* Quick Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: "#e3f2fd", height: "100%" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <AccountBalanceIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="primary">System Balance</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {formatNumber(reportData.summary.systemBalance)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    From {reportData.systemBalanceBreakdown.transactions.length} total transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: "#e8f5e9", height: "100%" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <AttachMoneyIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="success.main">Actual Cash</Typography>
                  </Box>
                  <Typography variant="h4" color="success.main">
                    {formatNumber(reportData.summary.actualCash)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {reportData.summary.actualCashMethod === "denomination" 
                      ? "Counted by denominations" 
                      : "Direct entry"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                bgcolor: reportData.summary.varianceType === "surplus" ? "#fff3e0" : 
                         reportData.summary.varianceType === "deficit" ? "#ffebee" : "#f5f5f5",
                height: "100%" 
              }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <CompareArrowsIcon 
                      color={reportData.summary.varianceType === "surplus" ? "warning" : "error"} 
                      sx={{ mr: 1 }} 
                    />
                    <Typography 
                      variant="h6" 
                      color={reportData.summary.varianceType === "surplus" ? "warning.main" : "error.main"}
                    >
                      Variance
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h4" 
                    color={reportData.summary.varianceType === "surplus" ? "warning.main" : "error.main"}
                  >
                    {formatNumber(reportData.summary.variance)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {reportData.summary.varianceType === "surplus" ? "Surplus" : 
                     reportData.summary.varianceType === "deficit" ? "Deficit" : "Balanced"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Basic Info */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: "#f5f5f5" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Account</Typography>
                <Typography variant="h6">
                  {reportData.summary.accountName} ({reportData.summary.accountNo})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Reconciliation Date</Typography>
                <Typography variant="h6">
                  {new Date(reportData.summary.reconciliationDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip 
                  label={reportData.summary.status} 
                  color="success" 
                  size="small" 
                />
              </Grid>
            </Grid>
          </Paper>

          {/* ==================== SECTION 1: SYSTEM BALANCE ==================== */}
          <Accordion 
            expanded={expandedSection === "system"}
            onChange={() => setExpandedSection(expandedSection === "system" ? "" : "system")}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#e3f2fd" }}>
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <AccountBalanceIcon color="primary" sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" color="primary">
                    Section 1: System Balance Calculation
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {reportData.systemBalanceBreakdown.explanation}
                  </Typography>
                </Box>
                <Typography variant="h5" color="primary" sx={{ mr: 2 }}>
                  {formatNumber(reportData.systemBalanceBreakdown.total)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell align="right"><strong>Debit (+)</strong></TableCell>
                      <TableCell align="right"><strong>Credit (-)</strong></TableCell>
                      <TableCell align="right"><strong>Running Balance</strong></TableCell>
                      <TableCell><strong>Explanation</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.systemBalanceBreakdown.transactions.map((txn, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{new Date(txn.date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={txn.type}
                            size="small"
                            color={txn.type === "Receipt" ? "success" : "error"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{txn.category}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Tooltip title={txn.description} arrow>
                            <Typography variant="body2" noWrap>
                              {txn.description}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ color: "success.main" }}>
                          {txn.debit > 0 ? formatNumber(txn.debit) : "-"}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "error.main" }}>
                          {txn.credit > 0 ? formatNumber(txn.credit) : "-"}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatNumber(txn.runningBalance)}</strong>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {txn.explanation}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell colSpan={6} align="right">
                        <strong>FINAL SYSTEM BALANCE:</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong style={{ fontSize: "1.1rem", color: "#1976d2" }}>
                          {formatNumber(reportData.systemBalanceBreakdown.total)}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <em>This is what the system calculated</em>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* ==================== SECTION 2: ACTUAL CASH ==================== */}
          <Accordion 
            expanded={expandedSection === "actual"}
            onChange={() => setExpandedSection(expandedSection === "actual" ? "" : "actual")}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#e8f5e9" }}>
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <AttachMoneyIcon color="success" sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" color="success.main">
                    Section 2: Actual Cash Counted
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Physical cash counted during reconciliation
                  </Typography>
                </Box>
                <Typography variant="h5" color="success.main" sx={{ mr: 2 }}>
                  {formatNumber(reportData.actualCashBreakdown.total)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Denomination / Method</strong></TableCell>
                    <TableCell align="center"><strong>Quantity</strong></TableCell>
                    <TableCell align="right"><strong>Value</strong></TableCell>
                    <TableCell><strong>Explanation</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.actualCashBreakdown.denominations.map((denom, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {denom.denomination === "Direct Entry" 
                          ? <Chip label="Direct Entry" color="success" size="small" />
                          : formatNumber(denom.denomination)
                        }
                      </TableCell>
                      <TableCell align="center">
                        {denom.denomination === "Direct Entry" ? "-" : denom.quantity}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatNumber(denom.value)}</strong>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {denom.explanation}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell colSpan={2} align="right">
                      <strong>TOTAL ACTUAL CASH:</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong style={{ fontSize: "1.1rem", color: "#2e7d32" }}>
                        {formatNumber(reportData.actualCashBreakdown.total)}
                      </strong>
                    </TableCell>
                    <TableCell>
                      <em>Physical cash counted</em>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </AccordionDetails>
          </Accordion>

          {/* ==================== SECTION 3: VARIANCE ==================== */}
          <Accordion 
            expanded={expandedSection === "variance"}
            onChange={() => setExpandedSection(expandedSection === "variance" ? "" : "variance")}
            sx={{ mb: 2 }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />} 
              sx={{ 
                bgcolor: reportData.varianceBreakdown.type === "surplus" ? "#fff3e0" : 
                         reportData.varianceBreakdown.type === "deficit" ? "#ffebee" : "#f5f5f5" 
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <CompareArrowsIcon 
                  color={reportData.varianceBreakdown.type === "surplus" ? "warning" : "error"} 
                  sx={{ mr: 2 }} 
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h6" 
                    color={reportData.varianceBreakdown.type === "surplus" ? "warning.main" : "error.main"}
                  >
                    Section 3: Variance Calculation
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Difference between Actual Cash and System Balance
                  </Typography>
                </Box>
                <Typography 
                  variant="h5" 
                  color={reportData.varianceBreakdown.type === "surplus" ? "warning.main" : "error.main"}
                  sx={{ mr: 2 }}
                >
                  {formatNumber(reportData.varianceBreakdown.difference)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert 
                    severity={reportData.varianceBreakdown.type === "surplus" ? "warning" : 
                             reportData.varianceBreakdown.type === "deficit" ? "error" : "success"}
                    icon={<InfoIcon />}
                  >
                    <Typography variant="body1">
                      <strong>{reportData.varianceBreakdown.explanation}</strong>
                    </Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Actual Cash (A)</Typography>
                      <Typography variant="h5" color="success.main">
                        {formatNumber(reportData.varianceBreakdown.actualCash)}
                      </Typography>
                      <Typography variant="caption">Physical cash counted</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">System Balance (B)</Typography>
                      <Typography variant="h5" color="primary">
                        {formatNumber(reportData.varianceBreakdown.systemBalance)}
                      </Typography>
                      <Typography variant="caption">Calculated from transactions</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Variance (A - B)</Typography>
                      <Typography 
                        variant="h5" 
                        color={reportData.varianceBreakdown.type === "surplus" ? "warning.main" : "error.main"}
                      >
                        {formatNumber(reportData.varianceBreakdown.difference)}
                      </Typography>
                      <Typography variant="caption">
                        {reportData.varianceBreakdown.type === "surplus" ? "Extra cash" : 
                         reportData.varianceBreakdown.type === "deficit" ? "Cash shortage" : "Balanced"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {reportData.summary.note && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
              <strong>Reconciliation Note:</strong> {reportData.summary.note}
            </Alert>
          )}

          {/* How to Read This Report */}
          <Paper sx={{ p: 3, bgcolor: "#f9f9f9" }}>
            <Typography variant="h6" gutterBottom>
              📊 How to Read This Report
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Section 1: System Balance
                </Typography>
                <Typography variant="body2">
                  Shows <strong>ALL transactions</strong> from account creation to the reconciliation date.
                  This is what your cash balance <strong>should be</strong> according to all recorded transactions.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  Section 2: Actual Cash
                </Typography>
                <Typography variant="body2">
                  Shows the <strong>physical cash counted</strong> during reconciliation. If counted by
                  denominations, each bill/coin type is listed. This is what you <strong>actually have</strong>.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="error.main" gutterBottom>
                  Section 3: Variance
                </Typography>
                <Typography variant="body2">
                  The <strong>difference</strong> between what you should have (System) and what you actually have (Actual).
                  Positive = Surplus (extra cash), Negative = Deficit (missing cash).
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </>
      ) : (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <AssessmentIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a reconciliation to view its detailed breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The report will show complete calculations for System Balance, Actual Cash, and Variance
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ReconciliationReportPage;
